import { Injectable, signal } from '@angular/core';
import { fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AppModeService } from './app-mode-service';
import { environment } from '@/environments/environment';

/**
 * Tipos de mensajes para comunicación iframe <-> host
 */
export type PostMessageType =
  | 'sc:init'
  | 'sc:ready'
  | 'sc:resize'
  | 'sc:requestToken'
  | 'sc:token'
  | 'sc:event'
  | 'sc:navigate';

export interface PostMessageData {
  type: PostMessageType;
  [key: string]: any;
}

export interface InitMessage extends PostMessageData {
  type: 'sc:init';
  user?: string;
  locale?: string;
  theme?: 'light' | 'dark';
  tenant?: string;
  preferredAuth?: 'bearer' | 'cookie';
}

export interface ResizeMessage extends PostMessageData {
  type: 'sc:resize';
  height: number;
}

export interface TokenMessage extends PostMessageData {
  type: 'sc:token';
  accessToken: string;
  refreshToken?: string;
}

export interface EventMessage extends PostMessageData {
  type: 'sc:event';
  name: 'lineAdded' | 'lineDeleted' | 'lineUpdated' | 'error';
  payload: any;
}

@Injectable({ providedIn: 'root' })
export class PostMessageService {
  private parentOrigin = signal<string>('*');
  private accessToken = signal<string | null>(null);
  private config = signal<InitMessage | null>(null);

  getAccessToken = this.accessToken.asReadonly();
  getConfig = this.config.asReadonly();

  constructor(private appMode: AppModeService) {
    const params = new URLSearchParams(window.location.search);

    // =====================================
    // 1) PREVIEW MODE
    // =====================================
    if (this.appMode.isPreview()) {
      this.loadPreviewConfig(params);
    }

    // =====================================
    // 2) UNIQUE MESSAGE LISTENER
    // =====================================
    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter((event) => this.isValidMessage(event)),
        map((event) => event.data as PostMessageData)
      )
      .subscribe((data) => this.handleMessage(data));
  }

  /** ============================================================
   *  📌 complete config when we are in PREVIEW MODE
   *     (app runing directly on the browser and not a widget)
   *  ============================================================ */
  private loadPreviewConfig(params: URLSearchParams): void {
    const token = params.get('token');
    const tenant = params.get('tenant') ?? 'CO'; // 👈
    const locale = params.get('locale') ?? 'es-CO';
    const theme = params.get('theme') ?? ('light' as any);
    const user = params.get('user');

    // 🔐 TOKEN handling
    if (token) {
      !environment.production &&
        !environment.production &&
        console.log('[Preview] Token por query param:', token);
      this.accessToken.set(token);
      localStorage.setItem('sc-token-dev', token);
    } else {
      const local = localStorage.getItem('sc-token-dev');
      if (local) {
        !environment.production &&
          console.log('[Preview] Usando token previamente guardado:', local);
        this.accessToken.set(local);
      }
    }

    // 🧩 CONFIG handling
    const config: InitMessage = {
      type: 'sc:init',
      tenant,
      locale,
      theme,
      preferredAuth: 'bearer',
      user: user ?? undefined,
    };

    this.config.set(config);

    !environment.production && console.log('[Preview] Config generada:', config);
  }

  private isValidMessage(event: MessageEvent): boolean {
    return event.data && typeof event.data === 'object' && event.data.type;
  }

  private isInitMessage(data: PostMessageData): data is InitMessage {
    return data.type === 'sc:init';
  }

  private isTokenMessage(data: PostMessageData): data is TokenMessage {
    return data.type === 'sc:token';
  }

  private handleMessage(data: PostMessageData): void {
    switch (data.type) {
      case 'sc:init':
        if (this.isInitMessage(data)) {
          this.config.set(data);
        }
        break;

      case 'sc:token':
        if (this.isTokenMessage(data)) {
          if (this.appMode.isEmbedded()) {
            !environment.production &&
              console.log('[PostMessage] 🔑 Token recibido (embebido):', data.accessToken);
          }

          this.accessToken.set(data.accessToken);
        }
        break;

      default:
        console.warn('[PostMessage] Tipo de mensaje desconocido:', data.type);
    }
  }

  private sendToParent(message: PostMessageData): void {
    if (!this.appMode.isEmbedded()) {
      return;
    }

    window.parent.postMessage(message, this.parentOrigin());
  }

  notifyReady(): void {
    this.sendToParent({ type: 'sc:ready' });
  }

  notifyResize(height: number): void {
    this.sendToParent({ type: 'sc:resize', height });
  }

  requestToken(): void {
    this.sendToParent({ type: 'sc:requestToken' });
  }

  emitEvent(name: string, payload: any): void {
    this.sendToParent({ type: 'sc:event', name, payload });
  }

  requestNavigation(path: string): void {
    this.sendToParent({ type: 'sc:navigate', path });
  }

  startAutoResize(): void {
    if (!this.appMode.isEmbedded()) return;

    const observer = new ResizeObserver(() => {
      const height = document.body.scrollHeight;
      this.notifyResize(height);
    });

    observer.observe(document.body);
  }
}
